import React, { useEffect, useState } from "react";
import {
 TextField,
 InputAdornment,
 IconButton,
 Popover,
 Box,
 SvgIcon,
 GlobalStyles,
} from "@mui/material";
import { DateCalendar, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LuCalendar } from "@/Components/Icons/Icons";
import FormLabel from "./FormLabel";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"; // for UTC parsing
import timezone from "dayjs/plugin/timezone"; // for tz support
import customParseFormat from "dayjs/plugin/customParseFormat";


dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);



export default function DateSelector({
 label = "",
 value: externalValue,
 onChange,
 sx = {},
 textFieldSx = {},
 width,
 height,
 inputSize = "small", 
 disabled,
 gap,
 required,
 maxDateToToday = false, // 👈 new prop
 maxDate,
 minDate
}) {
   
  const [anchorEl, setAnchorEl] = useState(null);
  const [value, setValue] = useState(null);

// Parse and sync external value
useEffect(() => {
  if (externalValue === null) {
    setValue(null);
    return;
  }

  if (externalValue) {
    const normalized = dayjs(externalValue, "DD-MM-YYYY");
    if (normalized.isValid()) {
       setValue(normalized);
     }
   }
 }, [externalValue]);


 // ✅ Show only date part in the input
 const formattedDate = value?.isValid() ? value.format("DD-MM-YYYY") : "";


 const handleOpen = (event) => setAnchorEl(event.currentTarget);
 const handleClose = () => setAnchorEl(null);


 const handleDateChange = (newValue) => {
   if (!newValue) return;


   setValue(newValue);


   if (onChange) {
     // Return full string including time (keeps old time if provided, else 00:00:00)
     const finalValue = dayjs(newValue)
     .hour(0)
     .minute(0)
     .second(0)
     .millisecond(0)
     .format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z";


     onChange(finalValue, newValue);
   }
 };

  const resolvedMinDate = minDate ? dayjs(minDate) : dayjs("1980-01-01");
  const resolvedMaxDate = maxDate ? dayjs(maxDate) : maxDateToToday ? dayjs() : dayjs().endOf("year");

 const CalendarIcon = () => (
   <SvgIcon>
     <LuCalendar />
   </SvgIcon>
 );


 return (
   <LocalizationProvider dateAdapter={AdapterDayjs}>
     <>
       {/* 🎯 Global overrides for Year & Month lists */}
       <GlobalStyles
         styles={{
           ".MuiYearCalendar-root": {
             display: "flex !important",
             flexDirection: "column !important",
             alignItems: "center !important",
             overflowY: "auto !important",
             maxHeight: "250px !important",
             paddingRight: "8px !important",
             width: "auto !important",
             columnGap: "0 !important", 
             rowGap: "0 !important",
             flexWrap: "nowrap !important", 
           },
           ".MuiMonthCalendar-root": {
             display: "flex !important",
             flexDirection: "column !important",
             alignItems: "center !important",
             overflowY: "auto !important",
             maxHeight: "250px !important",
             paddingRight: "8px !important",
             width: "auto !important", 
             columnGap: "0 !important",
             rowGap: "0 !important",
             flexWrap: "nowrap !important", 
           },
           ".MuiYearCalendar-root .MuiPickersYear-root": {
             width: "80px !important", 
             textAlign: "center !important",
             padding: "6px 0 !important",
             borderRadius: "6px !important",
             margin: "2px auto !important", 
           },
         }}
       />


       <Box
         display="flex"
         flexDirection="column"
         gap={label ? 1 : 0}
         width={width}
         height={height}
         sx={{
           ...sx,
         }}
       >
         {/* <FormLabel label={label}/> */}
         <FormLabel label={label} gap={gap} required={required}/>


         <Box onClick={(e)=> !disabled && handleOpen(e)}>
           <TextField
             disabled={disabled}
             value={formattedDate}
             fullWidth
             readOnly
             placeholder="DD:MM:YYYY"
             size={inputSize}
             InputProps={{
               endAdornment: (
                 <InputAdornment position="end">
                   <IconButton
                     disabled={disabled}
                     sx={{
                       "&.Mui-disabled": {
                         pointerEvents: "none",
                         "& .MuiSvgIcon-root": {
                           color: "var(--gray-350)",
                           cursor: "not-allowed",
                         },
                       },
                       "&:not(.Mui-disabled)": {
                         cursor: "pointer",
                         "& .MuiSvgIcon-root": {
                           color: "var(--gray-800)",
                         },
                       },
                     }}
                   >
                     <CalendarIcon />
                   </IconButton>
                 </InputAdornment>
               ),
             }}
             sx={{
               "& .MuiOutlinedInput-root": {
                 fontSize: "16px",
                 fontWeight: 400,
                 borderRadius: "8px",
                 bgcolor: disabled ? "var(--gray-100)" : "var(--gray-0)",
                 cursor: "pointer",
                 height: height || 40,
                 alignItems: "center",
                 "& fieldset": {
                   border: "1px solid var(--gray-300)",
                 },
                 "&:hover fieldset": {
                   borderColor: "var(--gray-300)",
                 },
                 "&.Mui-focused fieldset": {
                   borderColor: "var(--gray-300)",
                 },
                 ...(Boolean(anchorEl) && {
                   borderColor: "var(--brand-600) !important",
                   boxShadow: "0 0 0 4px var(--brand-200)",
                   "& fieldset": {
                     border: "1px solid var(--brand-600) !important",
                   },
                 }),
               },
               "& .MuiInputBase-input": {
                 padding: "12px 14px",
                 cursor: "pointer",
               },
               ...textFieldSx,
             }}
           />
         </Box>


         <Popover
           open={Boolean(anchorEl)}
           anchorEl={anchorEl}
           onClose={handleClose}
           anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
           PaperProps={{
             sx: {
               border: "1px solid var(--gray-300)",
               borderRadius: "8px",
               boxShadow: "0px 4px 12px var(--gray-300)",
               width: "auto",
               px : 2
             },
           }}
         >
           <Box sx={{ width: 250 }}>
             <DateCalendar
               disabled={disabled}
               value={value}
               onChange={(newValue, selectionState) => {
                 handleDateChange(newValue);
                 if (selectionState === "finish") handleClose();
               }}
               views={["year", "month", "day"]}
               minDate={resolvedMinDate}
                maxDate={resolvedMaxDate}
               shouldDisableYear={(year) =>
                 year.year() < 1980 || year.year() > dayjs().year()
               }
               slotProps={{
                 year: {
                   sx: {
                     width: "10%",
                     textAlign: "center",
                     padding: "6px 0",
                     borderRadius: "6px",
                   },
                 },
                 month: {
                   sx: {
                     width: "100%",
                     textAlign: "center",
                     padding: "6px 0",
                     borderRadius: "6px",
                   },
                 },
                 day: {
                   sx: {
                     "&.Mui-selected": {
                       backgroundColor: "var(--brand-700)",
                       color: "var(--gray-0)",
                       "&:hover": {
                         backgroundColor: "var(--brand-700)",
                       },
                     },
                   },
                 },
                 calendarHeader: {
                   sx: {
                     borderBottom: "1px solid var(--gray-300)",
                     px: 2,
                     pt: 1,
                     pb: 1.5,
                     position: "relative",
                     display: "flex",
                     justifyContent: "center",
                     alignItems: "center",
                     "& .MuiPickersCalendarHeader-labelContainer": {
                       display: "block",
                       textAlign: "center",
                       position: "absolute",
                     },
                     "& .MuiPickersCalendarHeader-switchViewButton": {
                       display: "none",
                     },
                     "& .MuiPickersArrowSwitcher-root": {
                       position: "absolute",
                       width: "100%",
                       display: "flex",
                       justifyContent: "space-between",
                       pointerEvents: "none",
                     },
                     "& .MuiIconButton-root": {
                       pointerEvents: "auto",
                       zIndex: 1,
                     },
                   },
                 },
               }}
               sx={{ width: "100%" }}
             />
           </Box>
         </Popover>
       </Box>
     </>
   </LocalizationProvider>
 );
}



